import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LayoutAlternative } from '@thive/fddm-types';
import { useShallow } from 'zustand/react/shallow';

import { PlusIcon } from '@/assets/icons';
import { theme } from '@/modules/common/theme';

import { FloorplanGridItem } from './FloorplanGridItem';
import { useFloorplanAdminStore } from './store';

const containerStyle = {
  marginBottom: '50px',
};

const listItemStyle = {
  width: '100%',
  height: 230,
  borderRadius: 0,
  backgroundColor: theme.palette.others.lighter,
  border: '1px solid',
  borderColor: theme.palette.neutral.grey,
  padding: 0,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.others.lighter,
    borderColor: theme.palette.primary.main,
  },
};

type Props = {
  alternatives: LayoutAlternative[];
  onOpenCreatorClick: () => void;
  openDuplicator: () => void;
};

export const FloorplanGrid = ({ alternatives, onOpenCreatorClick, openDuplicator }: Props) => {
  const { t } = useTranslation();
  const { selectedFloorplanIds, floorplansBeingDeletedIds } = useFloorplanAdminStore(
    useShallow((state) => ({
      selectedFloorplanIds: state.selectedFloorplanIdsState,
      floorplansBeingDeletedIds: state.floorplanIdsBeingDeletedState,
    })),
  );

  const handleBackdropCick = useCallback(() => {
    useFloorplanAdminStore.getState().setSelectedFloorplanIdsState([]);
  }, []);

  return (
    <Box
      display='grid'
      gridTemplateColumns='repeat(auto-fill,minmax(300px,1fr))'
      gap={4}
      data-testid='floorplans-list'
      onClick={handleBackdropCick}
      sx={containerStyle}
    >
      <Box>
        <Button
          onClick={onOpenCreatorClick}
          sx={listItemStyle}
          data-testid='create floorplan grid button'
        >
          <Stack direction='row' justifyContent='center' alignItems='center'>
            <PlusIcon sx={{ width: 25, height: 25, color: theme.palette.neutral.darker }} />
            <Typography fontSize={14} fontWeight={500} sx={{ ml: 1 }}>
              {t('interface:home.new_floorplan', 'New Floorplan')}
            </Typography>
          </Stack>
        </Button>
      </Box>
      {alternatives.length > 0 &&
        alternatives.map((item) => (
          <FloorplanGridItem
            layoutAlternative={item}
            key={item.alternativeId}
            isSelected={selectedFloorplanIds.includes(item.alternativeId)}
            isBeingDeleted={floorplansBeingDeletedIds.includes(item.alternativeId)}
            openDuplicator={openDuplicator}
          />
        ))}
    </Box>
  );
};
